import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Role } from 'context/authentication/Role.types'
import { outlineAnimation } from 'global/helpers/animations'
import { hasAccess } from 'global/helpers/authorization'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import StatusUpdateDialog from 'screens/StatusManager/StatusUpdateDialog'
import LocationLink from 'screens/TripDashboard/LocationLink'
import PrimaryActions from 'screens/TripDetails/PrimaryActions'
import StopHeader from 'screens/TripDetails/StopHeader'
import type { KNDataCardAction } from 'screens/TripDetails/TripDetails.types'
import type { LegCardCustomsStopProps } from './LegCard.types'

const LegCardCustomsStop = ({
  trip,
  leg,
  stop,
  weblinkToken,
  collapsed = false,
}: LegCardCustomsStopProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation<{ wayPointCids: string[] }>()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const statusUpdateDialog = useDialog<typeof StatusUpdateDialog>()
  const [isHighlighted, setIsHighlighted] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const actions: KNDataCardAction[] = []
  if (stop.availableStatuses.length > 0) {
    actions.push({
      label: t('screens.cs.trip_details.card.actions.update_status'),
      handler: statusUpdateDialog.handleOpen,
    })
  }

  useEffect(() => {
    setIsHighlighted(location.state?.wayPointCids.includes(stop.wayPointCid))
  }, [location.state])

  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView()
    }
  }, [isHighlighted])

  return (
    <>
      <Box
        ref={cardRef}
        sx={{
          width: '100%',
          padding: { xs: 2, md: 0 },
          animation: isHighlighted ? outlineAnimation() : undefined,
        }}
      >
        <Stack
          spacing={{ xs: 0, md: 2 }}
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Box sx={{ minWidth: '16rem' }}>
            <StopHeader leg={leg} stop={stop} />
          </Box>
          <Collapse in={!collapsed} timeout={200} sx={{ flexGrow: 1 }}>
            <Grid container spacing={{ xs: 0, md: 3 }} alignItems={{ md: 'center' }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="p6" color="primary.light" component="p">
                  {t('screens.cs.trip_details.card.location')}
                </Typography>
                <Typography color="text.main" variant="p3">
                  {stop.address.name.length > 0 ? stop.address.name.join(' ') : t('waypoints.types.CUS')}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography variant="p6" color="primary.light" component="p">
                  {t('screens.cs.trip_details.card.address')}
                </Typography>
                <LocationLink
                  countryCode={stop.address.countryCode}
                  city={stop.address.city.join(', ')}
                  zipCode={stop.address.zipCode}
                  street={stop.address.street.join(', ')}
                  marker={stop.geoPoint ? {
                    id: stop.wayPointCid,
                    latitude: stop.geoPoint.latitude,
                    longitude: stop.geoPoint.longitude,
                    type: stop.type,
                  } : undefined}
                  lineBreaks={isMobile ? 'all' : 'street'}
                />
              </Grid>
            </Grid>
          </Collapse>
        </Stack>
        {actions.length > 0 && (hasAccess(Role.Editor) || weblinkToken) && (
          <PrimaryActions actions={actions} sx={{ paddingTop: { xs: 2, md: 0 } }} />
        )}
      </Box>

      <StatusUpdateDialog
        payload={{
          trip,
          leg,
          stop,
          weblinkToken,
        }}
        open={statusUpdateDialog.isOpen}
        onAction={statusUpdateDialog.handleAction}
        onClose={statusUpdateDialog.handleClose}
      />
    </>
  )
}

export default LegCardCustomsStop
